import {
  ContentfulComponent,
  ContentfulRichTextBlock,
  Cta,
  ContentfulModule,
  MediaAsset,
  Sys,
  HyperlinkedMediaProps,
  AspectRatio,
  DocumentWithHyperlinkOrDisclosureEntries,
} from 'libs/contentful/types';
import { YouTubeEmbedProps } from 'components/shared/YouTubeEmbed/types';
import { Margins, Colorways, ImageWidth } from 'types/constants';
import { RecursivePartial } from 'utils/recursivePartial';
import { VideoItemReference } from '@/types/sharedComponents';

export enum CardVariant {
  Icon = 'Icon',
  Image = 'Image',
  Text = 'Text',
  Video = 'Video',
}

export enum CardStyle {
  Stepped = 'Stepped',
  Paginated = 'Paginated',
  None = 'None',
}

export enum HyperlinkedImagesLocation {
  Top = 'Top',
  Bottom = 'Bottom',
}

export interface CardWrapperClassNames {
  wrapper: string;
  header: string;
  content: string;
}

export interface CardContentClassNames {
  wrapper: string;
  header: {
    wrapper: string;
    eyebrow: string;
    headline: string;
  };

  copy: string;
}

export interface CardContentProps {
  sys: Sys;

  showFiveStarEyebrow?: boolean;
  showChimeLogo?: boolean;
  eyebrow?: ContentfulRichTextBlock;

  headline: ContentfulRichTextBlock;
  copy?: DocumentWithHyperlinkOrDisclosureEntries;

  className?: RecursivePartial<CardContentClassNames>;
  testId?: string;
}

export interface CardCTAClassNames {
  wrapper: string;
}

export interface CardCTAProps {
  sys?: Sys;

  cta: Cta;
  cardVariant: CardVariant;
  className?: RecursivePartial<CardCTAClassNames>;
}

export interface CardClassNames {
  cardWrapper: CardWrapperClassNames;
  cardContent: CardContentClassNames;
}

export interface IconVariantClassNames extends CardClassNames {
  icon: string;
  eyebrow: string;
  cta: string;

  hyperlinkedImages: string;
}

export interface ImageVariantClassNames extends CardClassNames {
  media: {
    wrapper: string;
    content: string;
  };
}

export type TextVariantClassNames = CardClassNames;

export interface VideoVariantClassNames extends CardClassNames {
  transitions: {
    playing: string;
    paused: string;
  };
  media: {
    wrapper: string;
    content: string;
  };
  preview: {
    label: string;
  };
}

export interface CardCarouselClassNames {
  wrapper: string;
  carousel: string;
  pagination: string;
  cardGrid: string;
  iconVariant: RecursivePartial<IconVariantClassNames>;
  imageVariant: RecursivePartial<ImageVariantClassNames>;
  textVariant: RecursivePartial<TextVariantClassNames>;
  videoVariant: RecursivePartial<VideoVariantClassNames>;
}

export interface CardWrapperProps {
  sys?: Sys;

  cardStyle?: CardStyle;

  index?: number;
  total?: number;

  color: Colorways;
  children: React.ReactNode;
  className?: RecursivePartial<CardWrapperClassNames>;

  testId?: string;
}

export interface CardProps extends ContentfulComponent {
  index: number;
  total: number;

  variant: CardVariant;

  cardStyle: CardStyle;
  color: Colorways;

  headline: ContentfulRichTextBlock;
  copy: DocumentWithHyperlinkOrDisclosureEntries;
}

export interface IconVariantProps extends CardProps {
  variant: CardVariant.Icon;
  iconImage?: MediaAsset;

  showFiveStarEyebrow?: boolean;
  showChimeLogo?: boolean;
  eyebrow?: ContentfulRichTextBlock;

  hyperlinkedImages?: {
    items: HyperlinkedMediaProps[];
  };

  hyperlinkedImagesLocation?: HyperlinkedImagesLocation;

  cta: Cta;
  className?: RecursivePartial<IconVariantClassNames>;
}

export interface ImageVariantProps extends CardProps {
  variant: CardVariant.Image;
  desktopMedia?: MediaAsset;
  mobileMedia?: MediaAsset;
  desktopAspectRatio?: AspectRatio;
  mobileAspectRatio?: AspectRatio;

  imageSize?: ImageWidth;
  className?: RecursivePartial<ImageVariantClassNames>;
}

export interface TextVariantProps extends CardProps {
  variant: CardVariant.Text;
  className?: RecursivePartial<TextVariantClassNames>;
}

export interface VideoVariantProps extends CardProps {
  variant: CardVariant.Video;
  video?: VideoItemReference | YouTubeEmbedProps;
  className?: RecursivePartial<VideoVariantClassNames>;
}

export type CardVariantProps = IconVariantProps | ImageVariantProps | TextVariantProps | VideoVariantProps;

export type CarouselPaginationThresholdProp = (isAboveLg: boolean, isAboveMd: boolean) => number;

export interface CardCarouselProps extends ContentfulModule {
  color: Colorways;
  margins: Margins;
  cardStyle: CardStyle;
  cards: { items: CardVariantProps[] };
  className?: RecursivePartial<CardCarouselClassNames>;

  paginationPortalRef?: React.RefObject<Element | null>;
  getCarouselPaginationThreshold?: CarouselPaginationThresholdProp;
  testId?: string;
}
